import React, { useEffect, useState, useRef } from 'react';
import {
  Button, Form, Table,
} from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import {
  Chart, BarElement, CategoryScale, LinearScale, Tooltip, Legend,
} from 'chart.js';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';
import Error from '../../Components/Error';
import { get } from '../../Services/API';
import Success from '../../Components/Succes';
import {
  toUtcIsoString, DEFAULT_TIMEZONE,
} from '../../Utils/timezone';
import thousandSeparator from '../../Utils/numberFormat';
import PieChart from '../../Components/PieChart';
import UserService from '../../Services/UserService';
import Permissions from '../../Utils/Permissions';

/* eslint-disable no-underscore-dangle, no-nested-ternary --
   Trip._id is the booking id surfaced from the backend (Mongo convention);
   the nested ternaries below are short sign-of-amount classifications and
   read more naturally inline than as helper functions. */

Chart.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

dayjs.extend(utc);
dayjs.extend(tz);

function channelLabel(ch) {
  if (ch === 'PH_DASH') return 'Manual (Dash)';
  if (ch === 'PH_WEB') return 'Website';
  return ch;
}

function cancelBadgeColor(rate) {
  if (rate > 20) return 'danger';
  if (rate > 10) return 'warning';
  return 'success';
}

function sortedRows(rows, { field, dir }) {
  return [...rows].sort((a, b) => {
    const av = a[field] ?? 0;
    const bv = b[field] ?? 0;
    return dir === 'asc' ? av - bv : bv - av;
  });
}

function SortTh({
  label, field, sort, onSort,
}) {
  const active = sort.field === field;
  return (
    <th
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      onClick={() => onSort({ field, dir: active && sort.dir === 'desc' ? 'asc' : 'desc' })}
    >
      {label}
      {' '}
      {active && <i className={`fa fa-arrow-${sort.dir === 'asc' ? 'up' : 'down'} fa-xs`} />}
    </th>
  );
}

function pct(curr, prev) {
  if (!prev) return null;
  return Math.round(((curr - prev) / Math.abs(prev)) * 100);
}

function StatTrend({ curr, prev, label }) {
  const p = pct(curr, prev);
  if (p === null) return null;
  const up = p >= 0;
  return (
    <span className={`stat-trend ${up ? 'stat-trend-up' : 'stat-trend-down'}`}>
      <i className={`fa fa-arrow-${up ? 'up' : 'down'}`} />
      {' '}
      {Math.abs(p)}
      %
      {' '}
      <span className="stat-trend-label">{label}</span>
    </span>
  );
}

function Stats() {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const currentYear = dayjs().year();
  const years = Array.from({ length: 6 }, (_, i) => currentYear + 1 - i);

  const [loadIn] = useState(true);
  const [globalStats, setGlobalStats] = useState(null);
  const [quarterlyStats, setQuarterlyStats] = useState(null);
  const [monthlyRevenue, setMonthlyRevenue] = useState({ current: [], lastYear: [] });
  const [analyticsStats, setAnalyticsStats] = useState(null);
  const [employeePerf, setEmployeePerf] = useState(null);
  const [employeePerfGranularity, setEmployeePerfGranularity] = useState('month');
  const [employeePerfPeriods, setEmployeePerfPeriods] = useState(6);
  const [employeePerfLoading, setEmployeePerfLoading] = useState(false);
  const [employeePerfSelected, setEmployeePerfSelected] = useState([]);
  const [partnerSort, setPartnerSort] = useState({ field: 'revenue', dir: 'desc' });
  const [partnerLimit, setPartnerLimit] = useState(5);
  const [creatorSort, setCreatorSort] = useState({ field: 'total_revenue', dir: 'desc' });
  const [creatorLimit, setCreatorLimit] = useState(5);
  const [categoryLimit, setCategoryLimit] = useState(5);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [selectedMonth, setSelectedMonth] = useState(dayjs().month());
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [showMonthPicker, setShowMonthPicker] = useState(false);

  const loadedGlobalStats = useRef(null);

  const fetchMonthlyRevenue = async () => {
    const buildMonthPromise = (d) => {
      const startDate = d.startOf('month');
      const endDate = d.endOf('month');
      return get('/stats', {
        type: 'global_stats',
        filters: {
          date: [toUtcIsoString(startDate), toUtcIsoString(endDate)],
          pageSize: 10000,
          status: ['Confirmed', 'Completed', 'Cancelled', 'Passenger No-Show'],
          sort: { date_datetime: 'desc' },
        },
      }).then((res) => ({
        label: d.format('MMM YYYY'),
        revenue: res.data?.data?.total_revenue ?? 0,
        profit: res.data?.data?.total_profit ?? 0,
      }));
    };

    try {
      const currentPromises = Array.from({ length: 6 }, (_, i) => buildMonthPromise(
        dayjs().tz(DEFAULT_TIMEZONE).subtract(i, 'month'),
      ));
      const lastYearPromises = Array.from({ length: 6 }, (_, i) => buildMonthPromise(
        dayjs().tz(DEFAULT_TIMEZONE).subtract(i, 'month').subtract(1, 'year'),
      ));
      const [currentResults, lastYearResults] = await Promise.all([
        Promise.all(currentPromises),
        Promise.all(lastYearPromises),
      ]);
      setMonthlyRevenue({
        current: currentResults.reverse(),
        lastYear: lastYearResults.reverse(),
      });
    } catch (err) {
      setError(err.response?.data?.message);
    }
  };

  const fetchAnalyticsStats = async () => {
    try {
      const startDate = dayjs().tz(DEFAULT_TIMEZONE).subtract(6, 'month').startOf('month');
      const endDate = dayjs().tz(DEFAULT_TIMEZONE).endOf('day');
      const response = await get('/stats', {
        type: 'analytics_stats',
        filters: {
          date: [toUtcIsoString(startDate), toUtcIsoString(endDate)],
          pageSize: 10000,
          status: ['Confirmed', 'Completed', 'Cancelled', 'Passenger No-Show', 'Driver No-Show'],
          sort: { date_datetime: 'desc' },
        },
      });
      setAnalyticsStats(response.data?.data);
    } catch (err) {
      setError(err.response?.data?.message);
    }
  };

  const fetchEmployeePerformance = async (
    granularity = employeePerfGranularity,
    periods = employeePerfPeriods,
  ) => {
    setEmployeePerfLoading(true);
    try {
      const response = await get('/stats', {
        type: 'employee_performance_stats',
        granularity,
        periods,
        filters: {
          pageSize: 10000,
          status: ['Confirmed', 'Completed', 'Passenger No-Show', 'Driver No-Show'],
        },
      });
      setEmployeePerf(response.data?.data);
    } catch (err) {
      setError(err.response?.data?.message);
    } finally {
      setEmployeePerfLoading(false);
    }
  };

  const fetchGlobalStatsPeriod = (month, year) => {
    const base = dayjs().tz(DEFAULT_TIMEZONE).year(year).month(month);
    const startDate = base.startOf('month');
    const endDate = base.endOf('month');
    return get('/stats', {
      type: 'global_stats',
      filters: {
        date: [toUtcIsoString(startDate), toUtcIsoString(endDate)],
        pageSize: 10000,
        status: ['Confirmed', 'Completed', 'Cancelled', 'Passenger No-Show'],
        sort: { date_datetime: 'desc' },
      },
    }).then((res) => res.data?.data);
  };

  const fetchQuarterlyStats = async (month, year) => {
    // Compute quarter boundaries manually — dayjs has no built-in quarter support
    const qNum = Math.floor(month / 3) + 1;
    const qStartMonth = (qNum - 1) * 3; // 0, 3, 6, or 9
    const qEndMonth = qStartMonth + 2; // 2, 5, 8, or 11
    const quarterStart = dayjs().tz(DEFAULT_TIMEZONE).year(year).month(qStartMonth)
      .startOf('month');
    const quarterEnd = dayjs().tz(DEFAULT_TIMEZONE).year(year).month(qEndMonth)
      .endOf('month');
    const prevQuarterStart = quarterStart.subtract(3, 'month').startOf('month');
    const prevQuarterEnd = quarterStart.subtract(1, 'month').endOf('month');

    const fetchQuarter = (start, end) => get('/stats', {
      type: 'global_stats',
      filters: {
        date: [toUtcIsoString(start), toUtcIsoString(end)],
        pageSize: 10000,
        status: ['Confirmed', 'Completed', 'Cancelled', 'Passenger No-Show'],
        sort: { date_datetime: 'desc' },
      },
    }).then((res) => res.data?.data);

    try {
      const [current, lastYear, prevQuarter] = await Promise.all([
        fetchQuarter(quarterStart, quarterEnd),
        fetchQuarter(quarterStart.subtract(1, 'year'), quarterEnd.subtract(1, 'year')),
        fetchQuarter(prevQuarterStart, prevQuarterEnd),
      ]);
      const prevQNum = qNum === 1 ? 4 : qNum - 1;
      const prevQYear = qNum === 1 ? year - 1 : year;
      setQuarterlyStats({
        current, lastYear, prevQuarter, quarter: qNum, year, prevQNum, prevQYear,
      });
    } catch (err) {
      setError(err.response?.data?.message);
    }
  };

  const handleGlobalStatsLoad = async (month, year) => {
    try {
      const prevMonth = dayjs().tz(DEFAULT_TIMEZONE).year(year).month(month)
        .subtract(1, 'month');
      const [current, lastMonth, lastYear] = await Promise.all([
        fetchGlobalStatsPeriod(month, year),
        fetchGlobalStatsPeriod(prevMonth.month(), prevMonth.year()),
        fetchGlobalStatsPeriod(month, year - 1),
      ]);
      setGlobalStats({ current, lastMonth, lastYear });
    } catch (err) {
      setError(err.response?.data?.message);
    }
  };

  const canSeeGlobal = UserService.getInstance().canAccessModule(Permissions.CAN_SEE_GLOBAL_STATS);
  const canSeeBasic = UserService.getInstance().canAccessModule(Permissions.CAN_SEE_BASIC_STATS);
  const canSeePax = UserService.getInstance().canAccessModule(Permissions.CAN_SEE_PAX_STATS);

  useEffect(() => {
    if (canSeeGlobal) {
      fetchMonthlyRevenue();
    }
    if (canSeePax) {
      fetchAnalyticsStats();
    }
    if (canSeeGlobal) {
      fetchEmployeePerformance();
    }
  }, [loadIn]);

  useEffect(() => {
    const key = `${selectedMonth}-${selectedYear}`;
    if (loadedGlobalStats.current === key) {
      return;
    }
    loadedGlobalStats.current = key;
    if (canSeeBasic) {
      handleGlobalStatsLoad(selectedMonth, selectedYear);
      fetchQuarterlyStats(selectedMonth, selectedYear);
    }
  }, [selectedMonth, selectedYear]);

  return (
    <>
      {/* Hero Section */}
      <div className="hero-section mb-4">
        <div className="row align-items-center">
          <div className="col-lg-8 mb-3 mb-lg-0">
            <h1 className="display-5 fw-bold mb-3">
              Statistics
              {' '}
              <span className="hero-highlight">Dashboard</span>
            </h1>
            <p className="lead text-muted mb-4">
              View analytics, reports, and performance metrics
            </p>
          </div>
          <div className="col-lg-4 d-none d-lg-block text-end">
            <div className="hero-icon">
              <i className="fa fa-chart-line fa-5x text-primary opacity-25" />
            </div>
          </div>
        </div>
      </div>

      <Error
        error={error}
        setError={setError}
      />

      <Success
        success={success}
        setSuccess={setSuccess}
      />

      {canSeeBasic && (
      <div className="stats-page">
        {/* Month / Year selector */}
        <div className="month-nav mb-4">
          <Button
            variant="outline-secondary"
            size="sm"
            className="month-nav-btn"
            onClick={() => {
              const d = dayjs().year(selectedYear).month(selectedMonth).subtract(1, 'month');
              setSelectedMonth(d.month());
              setSelectedYear(d.year());
              loadedGlobalStats.current = null;
            }}
          >
            <i className="fa fa-chevron-left" />
          </Button>

          {showMonthPicker ? (
            <span className="month-nav-picker">
              <Form.Select
                size="sm"
                className="month-nav-select"
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(Number(e.target.value));
                  loadedGlobalStats.current = null;
                }}
              >
                {months.map((m, i) => (
                  <option key={m} value={i}>{m}</option>
                ))}
              </Form.Select>
              <Form.Select
                size="sm"
                className="month-nav-select"
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(Number(e.target.value));
                  loadedGlobalStats.current = null;
                }}
              >
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Form.Select>
              <Button
                variant="link"
                size="sm"
                className="month-nav-done"
                onClick={() => setShowMonthPicker(false)}
              >
                <i className="fa fa-check" />
              </Button>
            </span>
          ) : (
            <button
              type="button"
              className="month-nav-label month-nav-label-btn"
              onClick={() => setShowMonthPicker(true)}
            >
              {months[selectedMonth]}
              {' '}
              {selectedYear}
            </button>
          )}

          <Button
            variant="outline-secondary"
            size="sm"
            className="month-nav-btn"
            onClick={() => {
              const d = dayjs().year(selectedYear).month(selectedMonth).add(1, 'month');
              setSelectedMonth(d.month());
              setSelectedYear(d.year());
              loadedGlobalStats.current = null;
            }}
          >
            <i className="fa fa-chevron-right" />
          </Button>
        </div>

        {globalStats && (() => {
          const { current, lastMonth, lastYear } = globalStats;

          return (
            <>
              {/* KPI tiles + Quarterly */}
              <div className="row g-3 mb-4">
                {[
                  {
                    label: 'Revenue',
                    value: current.total_revenue,
                    prevMonth: lastMonth?.total_revenue,
                    prevYear: lastYear?.total_revenue,
                    color: 'info',
                    icon: 'fa-euro-sign',
                    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    visible: canSeeGlobal,
                    breakdown: {
                      transfer: current.transfer_revenue,
                      experience: current.experience_revenue,
                    },
                  },
                  {
                    label: 'Profit',
                    value: current.total_profit,
                    prevMonth: lastMonth?.total_profit,
                    prevYear: lastYear?.total_profit,
                    color: 'success',
                    icon: 'fa-sack-dollar',
                    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    visible: canSeeGlobal,
                    breakdown: {
                      transfer: current.transfer_profit,
                      experience: current.experience_profit,
                    },
                  },
                  {
                    label: 'Transfers',
                    value: current.amount_of_transfers,
                    prevMonth: lastMonth?.amount_of_transfers,
                    prevYear: lastYear?.amount_of_transfers,
                    color: 'warning',
                    icon: 'fa-route',
                    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    visible: canSeeBasic,
                  },
                  {
                    label: 'Experiences',
                    value: current.amount_of_experiences,
                    prevMonth: lastMonth?.amount_of_experiences,
                    prevYear: lastYear?.amount_of_experiences,
                    color: 'primary',
                    icon: 'fa-star',
                    gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                    visible: canSeeBasic,
                  },
                ].filter(({ visible }) => visible).map(({
                  label, value, prevMonth: pm, prevYear: py, color, icon, gradient, breakdown,
                }) => (
                  <div key={label} className="col-12 col-sm-6 col-md-3">
                    <div className="stat-card">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <span className="stat-card-label">{label}</span>
                        <div className="stat-card-icon-sm" style={{ background: gradient }}>
                          <i className={`fa ${icon}`} />
                        </div>
                      </div>
                      <div className={`stat-card-value text-${color} mb-2`}>
                        {thousandSeparator(Math.round(value))}
                        {(icon === 'fa-euro-sign' || icon === 'fa-sack-dollar') && (
                          <span className="stat-currency">€</span>
                        )}
                      </div>
                      {breakdown
                        && (breakdown.transfer != null || breakdown.experience != null) && (
                        <div
                          className="d-flex justify-content-between small text-muted mb-2"
                          style={{ fontSize: '0.75rem', borderTop: '1px solid #eee', paddingTop: '6px' }}
                        >
                          <span title="Transfers">
                            <i className="fa fa-route me-1" />
                            €
                            {thousandSeparator(Math.round(breakdown.transfer || 0))}
                          </span>
                          <span title="Experiences">
                            <i className="fa fa-star me-1" />
                            €
                            {thousandSeparator(Math.round(breakdown.experience || 0))}
                          </span>
                        </div>
                      )}
                      <div className="d-flex flex-wrap gap-1">
                        <StatTrend curr={value} prev={pm} label="vs last mo." />
                        <StatTrend curr={value} prev={py} label="vs last yr." />
                      </div>
                    </div>
                  </div>
                ))}
                {quarterlyStats && [
                  {
                    label: `Q${quarterlyStats.quarter} ${quarterlyStats.year} Revenue`,
                    value: quarterlyStats.current?.total_revenue,
                    prevQ: quarterlyStats.prevQuarter?.total_revenue,
                    prevYear: quarterlyStats.lastYear?.total_revenue,
                    isCurrency: true,
                    color: 'info',
                    icon: 'fa-euro-sign',
                    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    visible: canSeeGlobal,
                    breakdown: {
                      transfer: quarterlyStats.current?.transfer_revenue,
                      experience: quarterlyStats.current?.experience_revenue,
                    },
                  },
                  {
                    label: `Q${quarterlyStats.quarter} ${quarterlyStats.year} Profit`,
                    value: quarterlyStats.current?.total_profit,
                    prevQ: quarterlyStats.prevQuarter?.total_profit,
                    prevYear: quarterlyStats.lastYear?.total_profit,
                    isCurrency: true,
                    color: 'success',
                    icon: 'fa-sack-dollar',
                    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    visible: canSeeGlobal,
                    breakdown: {
                      transfer: quarterlyStats.current?.transfer_profit,
                      experience: quarterlyStats.current?.experience_profit,
                    },
                  },
                  {
                    label: `Q${quarterlyStats.quarter} ${quarterlyStats.year} Transfers`,
                    value: quarterlyStats.current?.amount_of_transfers,
                    prevQ: quarterlyStats.prevQuarter?.amount_of_transfers,
                    prevYear: quarterlyStats.lastYear?.amount_of_transfers,
                    isCurrency: false,
                    color: 'warning',
                    icon: 'fa-route',
                    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    visible: canSeeBasic,
                  },
                  {
                    label: `Q${quarterlyStats.quarter} ${quarterlyStats.year} Experiences`,
                    value: quarterlyStats.current?.amount_of_experiences,
                    prevQ: quarterlyStats.prevQuarter?.amount_of_experiences,
                    prevYear: quarterlyStats.lastYear?.amount_of_experiences,
                    isCurrency: false,
                    color: 'primary',
                    icon: 'fa-star',
                    gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                    visible: canSeeBasic,
                  },
                ].filter(({ visible }) => visible).map(({
                  label, value, prevQ, prevYear, isCurrency, color, icon, gradient, breakdown,
                }) => (
                  <div key={label} className="col-12 col-sm-6 col-md-3">
                    <div className="stat-card">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <span className="stat-card-label">{label}</span>
                        <div className="stat-card-icon-sm" style={{ background: gradient }}>
                          <i className={`fa ${icon}`} />
                        </div>
                      </div>
                      <div className={`stat-card-value text-${color} mb-2`}>
                        {thousandSeparator(Math.round(value ?? 0))}
                        {isCurrency && <span className="stat-currency">€</span>}
                      </div>
                      {breakdown
                        && (breakdown.transfer != null || breakdown.experience != null) && (
                        <div
                          className="d-flex justify-content-between small text-muted mb-2"
                          style={{ fontSize: '0.75rem', borderTop: '1px solid #eee', paddingTop: '6px' }}
                        >
                          <span title="Transfers">
                            <i className="fa fa-route me-1" />
                            €
                            {thousandSeparator(Math.round(breakdown.transfer || 0))}
                          </span>
                          <span title="Experiences">
                            <i className="fa fa-star me-1" />
                            €
                            {thousandSeparator(Math.round(breakdown.experience || 0))}
                          </span>
                        </div>
                      )}
                      <div className="d-flex flex-wrap gap-1">
                        <StatTrend
                          curr={value}
                          prev={prevQ}
                          label={`vs Q${quarterlyStats.prevQNum} ${quarterlyStats.prevQYear}`}
                        />
                        <StatTrend
                          curr={value}
                          prev={prevYear}
                          label={`vs Q${quarterlyStats.quarter} ${quarterlyStats.year - 1}`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Charts */}
              {canSeeGlobal && (
              <div className="row g-3">
                <div className="col-12 col-md-6 col-xl-4">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body" style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
                      <h6 className="fw-bold mb-3">Revenue by Partners</h6>
                      <PieChart
                        data={current.revenue_by_partners.map((p) => ({
                          name: p.name,
                          value: p.revenue,
                        }))}
                      />
                    </div>
                  </div>
                </div>
                {current.profit_by_partners?.length > 0 && (
                  <div className="col-12 col-md-6 col-xl-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body" style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
                        <h6 className="fw-bold mb-3">Profit by Partners</h6>
                        <PieChart
                          data={current.profit_by_partners.map((p) => ({
                            name: p.name,
                            value: p.profit,
                          }))}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              )}
            </>
          );
        })()}
      </div>
      )}

      {canSeeGlobal && monthlyRevenue.current.length > 0 && (
        <div className="card shadow-sm rounded mt-4">
          <div className="card-body p-4" style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
            <h2 className="mb-4">Month over Month Revenue &amp; Profit</h2>
            <Bar
              data={{
                labels: monthlyRevenue.current.map((m) => m.label),
                datasets: [
                  {
                    label: 'Revenue',
                    data: monthlyRevenue.current.map((m) => m.revenue),
                    backgroundColor: 'rgba(54, 162, 235, 0.8)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1,
                  },
                  {
                    label: 'Revenue (prev. year)',
                    data: monthlyRevenue.lastYear.map((m) => m.revenue),
                    backgroundColor: 'rgba(54, 162, 235, 0.25)',
                    borderColor: 'rgba(54, 162, 235, 0.6)',
                    borderWidth: 1,
                    borderDash: [4, 4],
                  },
                  {
                    label: 'Profit',
                    data: monthlyRevenue.current.map((m) => m.profit),
                    backgroundColor: 'rgba(102, 126, 234, 0.8)',
                    borderColor: 'rgba(102, 126, 234, 1)',
                    borderWidth: 1,
                  },
                  {
                    label: 'Profit (prev. year)',
                    data: monthlyRevenue.lastYear.map((m) => m.profit),
                    backgroundColor: 'rgba(102, 126, 234, 0.25)',
                    borderColor: 'rgba(102, 126, 234, 0.6)',
                    borderWidth: 1,
                    borderDash: [4, 4],
                  },
                ],
              }}
              options={{
                responsive: true,
                plugins: {
                  legend: { position: 'top' },
                  tooltip: {
                    callbacks: {
                      label: (ctx) => `${ctx.dataset.label}: €${(ctx.raw ?? 0).toLocaleString()}`,
                    },
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: (value) => `€${value.toLocaleString()}`,
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      )}

      {/* ── Analytics sections ─────────────────────────────── */}
      {canSeeBasic && analyticsStats && (
        <>
          {/* 1. Cancellation & No-Show Analytics */}
          <div className="card shadow-sm rounded mt-4">
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-start mb-1">
                <h2 className="mb-0">Cancellation &amp; No-Show Analytics</h2>
                <span className="text-muted" style={{ fontSize: '0.8rem' }}>Last 6 months</span>
              </div>
              <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>
                Booking status breakdown per month, revenue lost to cancellations,
                and worst-performing partners.
              </p>

              {/* Summary tiles */}
              {(() => {
                const allMonths = analyticsStats.cancellation_stats.by_month;
                const totalBookings = allMonths.reduce((s, m) => s + m.total, 0);
                const totalCancelled = allMonths.reduce(
                  (s, m) => s + m.cancelled + m.passenger_no_show + m.driver_no_show,
                  0,
                );
                const totalRevenueLost = allMonths.reduce((s, m) => s + m.revenue_lost, 0);
                const overallRate = totalBookings > 0
                  ? Math.round((totalCancelled / totalBookings) * 100)
                  : 0;
                // eslint-disable-next-line no-nested-ternary
                const rateColor = overallRate > 15 ? 'danger' : overallRate > 8 ? 'warning' : 'success';
                return (
                  <div className="row g-3 mb-4">
                    {[
                      {
                        label: 'Cancellation Rate',
                        value: `${overallRate}%`,
                        color: rateColor,
                        icon: 'fa-ban',
                      },
                      {
                        label: 'Revenue Lost',
                        value: `€${thousandSeparator(Math.round(totalRevenueLost))}`,
                        color: 'danger',
                        icon: 'fa-circle-xmark',
                      },
                      {
                        label: 'Total Cancelled / NS',
                        value: totalCancelled,
                        color: 'warning',
                        icon: 'fa-triangle-exclamation',
                      },
                      {
                        label: 'Total Bookings', value: totalBookings, color: 'secondary', icon: 'fa-calendar-check',
                      },
                    ].map(({
                      label, value, color, icon,
                    }) => (
                      <div key={label} className="col-12 col-sm-6 col-md-3">
                        <div className={`border-start border-3 border-${color} ps-3 py-1`}>
                          <div
                            className="text-muted"
                            style={{
                              fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: 600,
                            }}
                          >
                            <i className={`fa ${icon} me-1`} />
                            {label}
                          </div>
                          <div className={`fw-bold text-${color}`} style={{ fontSize: '1.5rem', lineHeight: 1.2 }}>{value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Status bar chart by month */}
              <div style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
                <Bar
                  data={{
                    labels: analyticsStats.cancellation_stats.by_month.map((m) => m.month),
                    datasets: [
                      { label: 'Completed', data: analyticsStats.cancellation_stats.by_month.map((m) => m.completed), backgroundColor: 'rgba(25,135,84,0.75)' },
                      { label: 'Confirmed', data: analyticsStats.cancellation_stats.by_month.map((m) => m.confirmed), backgroundColor: 'rgba(13,110,253,0.75)' },
                      { label: 'Cancelled', data: analyticsStats.cancellation_stats.by_month.map((m) => m.cancelled), backgroundColor: 'rgba(220,53,69,0.75)' },
                      { label: 'Passenger No-Show', data: analyticsStats.cancellation_stats.by_month.map((m) => m.passenger_no_show), backgroundColor: 'rgba(255,152,0,0.75)' },
                      { label: 'Driver No-Show', data: analyticsStats.cancellation_stats.by_month.map((m) => m.driver_no_show), backgroundColor: 'rgba(108,117,125,0.75)' },
                    ],
                  }}
                  options={{
                    responsive: true,
                    plugins: { legend: { position: 'top' } },
                    scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true } },
                  }}
                />
              </div>

              {/* Partners with worst cancellation rates */}
              {analyticsStats.partner_performance?.some((p) => p.cancellation_rate > 0) && (
                <div className="mt-4">
                  <h6 className="fw-bold mb-2">Partners by Cancellation Rate</h6>
                  <Table size="sm" hover responsive className="responsive-card-table">
                    <thead>
                      <tr>
                        <th>Partner</th>
                        <th>Total Bookings</th>
                        <th>Cancelled / NS</th>
                        <th>Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...analyticsStats.partner_performance]
                        .sort((a, b) => b.cancellation_rate - a.cancellation_rate)
                        .slice(0, 8)
                        .map((p) => (
                          <tr key={p.uuid}>
                            <td data-label="Partner">{p.name}</td>
                            <td data-label="Total Bookings">{p.total_bookings}</td>
                            <td data-label="Cancelled / NS">{Math.round((p.cancellation_rate / 100) * p.total_bookings)}</td>
                            <td data-label="Rate">
                              <span className={`badge bg-${cancelBadgeColor(p.cancellation_rate)}`}>
                                {Math.round(p.cancellation_rate)}
                                %
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          </div>

          {canSeeGlobal && (
            <>
              {/* 2. Partner Performance Table */}
              <div className="card shadow-sm rounded mt-4">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-start mb-1">
                    <h2 className="mb-0">Partner Performance</h2>
                    <span className="text-muted" style={{ fontSize: '0.8rem' }}>Last 6 months</span>
                  </div>
                  <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>
                    Click any column header to sort.
                  </p>
                  <Table hover responsive size="sm" className="responsive-card-table">
                    <thead>
                      <tr>
                        <SortTh label="Partner" field="name" sort={partnerSort} onSort={setPartnerSort} />
                        <SortTh label="Bookings" field="total_bookings" sort={partnerSort} onSort={setPartnerSort} />
                        <SortTh label="Revenue" field="revenue" sort={partnerSort} onSort={setPartnerSort} />
                        <SortTh label="Avg Booking Value" field="avg_booking_value" sort={partnerSort} onSort={setPartnerSort} />
                        <SortTh label="Cancellation Rate" field="cancellation_rate" sort={partnerSort} onSort={setPartnerSort} />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows(analyticsStats.partner_performance, partnerSort)
                        .slice(0, partnerLimit)
                        .map((p) => (
                          <tr key={p.uuid}>
                            <td data-label="Partner" className="fw-semibold">{p.name}</td>
                            <td data-label="Bookings">{p.total_bookings}</td>
                            <td data-label="Revenue">
                              €
                              {thousandSeparator(Math.round(p.revenue))}
                            </td>
                            <td data-label="Avg Booking Value">
                              €
                              {thousandSeparator(Math.round(p.avg_booking_value))}
                            </td>
                            <td data-label="Cancellation Rate">
                              <span className={`badge bg-${cancelBadgeColor(p.cancellation_rate)}`}>
                                {Math.round(p.cancellation_rate)}
                                %
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                  {analyticsStats.partner_performance.length > partnerLimit && (
                    <div className="text-center mt-2">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setPartnerLimit((n) => n + 5)}
                      >
                        Load more
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* 3. Creator / Sales Leaderboard */}
              <div className="card shadow-sm rounded mt-4">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-start mb-1">
                    <h2 className="mb-0">Sales Leaderboard</h2>
                    <span className="text-muted" style={{ fontSize: '0.8rem' }}>Last 6 months</span>
                  </div>
                  <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>
                    Booking creators ranked by revenue generated. Click headers to re-sort.
                  </p>
                  {analyticsStats.creator_stats?.length > 0 && (
                    <div
                      className="mb-4"
                      style={{
                        minWidth: 0, maxWidth: '100%', overflow: 'hidden', margin: '0 auto',
                      }}
                    >
                      <PieChart
                        data={analyticsStats.creator_stats
                          .filter((c) => c.total_revenue > 0)
                          .map((c) => ({
                            name: c.created_by,
                            value: Math.round(c.total_revenue),
                          }))}
                      />
                    </div>
                  )}
                  <Table hover responsive size="sm" className="responsive-card-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <SortTh label="Creator" field="created_by" sort={creatorSort} onSort={setCreatorSort} />
                        <SortTh label="Bookings" field="total_bookings" sort={creatorSort} onSort={setCreatorSort} />
                        <SortTh label="Revenue" field="total_revenue" sort={creatorSort} onSort={setCreatorSort} />
                        <SortTh label="Avg Booking Value" field="avg_booking_value" sort={creatorSort} onSort={setCreatorSort} />
                        <SortTh label="Cancellation Rate" field="cancellation_rate" sort={creatorSort} onSort={setCreatorSort} />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows(analyticsStats.creator_stats, creatorSort)
                        .slice(0, creatorLimit)
                        .map((c, i) => (
                          <tr key={c.created_by}>
                            <td data-label="#" className="text-muted">{i + 1}</td>
                            <td data-label="Creator" className="fw-semibold">{c.created_by}</td>
                            <td data-label="Bookings">{c.total_bookings}</td>
                            <td data-label="Revenue">
                              €
                              {thousandSeparator(Math.round(c.total_revenue))}
                            </td>
                            <td data-label="Avg Booking Value">
                              €
                              {thousandSeparator(Math.round(c.avg_booking_value))}
                            </td>
                            <td data-label="Cancellation Rate">
                              <span className={`badge bg-${cancelBadgeColor(c.cancellation_rate)}`}>
                                {Math.round(c.cancellation_rate)}
                                %
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                  {analyticsStats.creator_stats.length > creatorLimit && (
                    <div className="text-center mt-2">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setCreatorLimit((n) => n + 5)}
                      >
                        Load more
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* 3b. Creator Revenue by Month (pivot table) */}
              {analyticsStats.creator_monthly_stats?.length > 0 && (() => {
                const allMonths = [...new Set(
                  analyticsStats.creator_monthly_stats.flatMap((r) => r.months.map((m) => m.month)),
                )];
                return (
                  <div className="card shadow-sm rounded mt-4">
                    <div className="card-body p-4">
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <h2 className="mb-0">Revenue by Employee &amp; Month</h2>
                        <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                          Last 6 months
                        </span>
                      </div>
                      <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                        Revenue generated per booking creator, broken down month by month.
                        Cancelled bookings excluded.
                      </p>
                      <div className="table-responsive">
                        <table className="table table-sm table-hover creator-month-table">
                          <thead>
                            <tr>
                              <th className="creator-month-name">Employee</th>
                              {allMonths.map((m) => (
                                <th key={m} className="text-end">{m}</th>
                              ))}
                              <th className="text-end">Total Revenue</th>
                              <th className="text-end">Total Profit</th>
                              <th className="text-end">Margin</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analyticsStats.creator_monthly_stats.map((row) => {
                              const byMonth = Object.fromEntries(
                                row.months.map((m) => [m.month, {
                                  revenue: m.revenue, profit: m.profit,
                                }]),
                              );
                              const maxRev = Math.max(...row.months.map((m) => m.revenue));
                              const totalMargin = row.total_revenue > 0
                                ? Math.round((row.total_profit / row.total_revenue) * 100) : 0;
                              return (
                                <tr key={row.created_by}>
                                  <td className="fw-semibold creator-month-name">
                                    {row.created_by}
                                  </td>
                                  {allMonths.map((m, idx) => {
                                    const rev = byMonth[m]?.revenue ?? 0;
                                    const prof = byMonth[m]?.profit ?? 0;
                                    const margin = rev > 0
                                      ? Math.round((prof / rev) * 100) : null;
                                    const intensity = maxRev > 0
                                      ? Math.round((rev / maxRev) * 100) : 0;
                                    const prevMonth = allMonths[idx - 1];
                                    const prevRev = prevMonth
                                      ? (byMonth[prevMonth]?.revenue ?? 0) : null;
                                    const momDiff = prevRev !== null
                                      && prevRev > 0
                                      && rev > 0
                                      ? Math.round(((rev - prevRev) / prevRev) * 100)
                                      : null;
                                    return (
                                      <td
                                        key={m}
                                        className="text-end"
                                        style={{
                                          background: rev > 0
                                            ? `rgba(13,110,253,${0.06 + intensity * 0.009})`
                                            : undefined,
                                        }}
                                      >
                                        {rev > 0 ? (
                                          <>
                                            <div className="d-flex align-items-center justify-content-end gap-1">
                                              <span>
                                                €
                                                {thousandSeparator(Math.round(rev))}
                                              </span>
                                              {momDiff !== null && (
                                                <span
                                                  className={momDiff >= 0 ? 'text-success' : 'text-danger'}
                                                  style={{ fontSize: '0.7em' }}
                                                  title={`${momDiff >= 0 ? '+' : ''}${momDiff}% vs prev month`}
                                                >
                                                  {momDiff >= 0 ? '▲' : '▼'}
                                                  {Math.abs(momDiff)}
                                                  %
                                                </span>
                                              )}
                                            </div>
                                            <div className="text-muted" style={{ fontSize: '0.75em' }}>
                                              €
                                              {thousandSeparator(Math.round(prof))}
                                              {margin !== null && (
                                                <span className="ms-1">
                                                  (
                                                  {margin}
                                                  %)
                                                </span>
                                              )}
                                            </div>
                                          </>
                                        ) : (
                                          <span className="text-muted">—</span>
                                        )}
                                      </td>
                                    );
                                  })}
                                  <td className="text-end fw-semibold">
                                    €
                                    {thousandSeparator(Math.round(row.total_revenue))}
                                  </td>
                                  <td className="text-end fw-semibold">
                                    €
                                    {thousandSeparator(Math.round(row.total_profit))}
                                  </td>
                                  <td className="text-end fw-semibold">
                                    {(() => {
                                      let marginClass = 'text-danger';
                                      if (totalMargin >= 30) marginClass = 'text-success';
                                      else if (totalMargin >= 15) marginClass = 'text-warning';
                                      return (
                                        <span className={marginClass}>
                                          {totalMargin}
                                          %
                                        </span>
                                      );
                                    })()}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* 3c. Employee Performance — configurable granularity */}
              {canSeeGlobal && employeePerf && (() => {
                const allRows = employeePerf.rows || [];
                const periodKeys = employeePerf.period_keys || [];
                const periodLabels = employeePerf.period_labels || [];
                const allCreators = allRows.map((r) => r.created_by);
                const filteredRows = employeePerfSelected.length > 0
                  ? allRows.filter((r) => employeePerfSelected.includes(r.created_by))
                  : allRows;
                const totalsByPeriod = periodKeys.map((_k, i) => filteredRows.reduce(
                  (acc, row) => {
                    const p = row.periods[i] || {};
                    return {
                      revenue: acc.revenue + (p.revenue || 0),
                      profit: acc.profit + (p.profit || 0),
                      transfer_count: acc.transfer_count + (p.transfer_count || 0),
                      experience_count: acc.experience_count + (p.experience_count || 0),
                    };
                  },
                  {
                    revenue: 0, profit: 0, transfer_count: 0, experience_count: 0,
                  },
                ));
                const grandTotals = filteredRows.reduce(
                  (acc, r) => ({
                    revenue: acc.revenue + r.total_revenue,
                    profit: acc.profit + r.total_profit,
                    transfers: acc.transfers + r.total_transfers,
                    experiences: acc.experiences + r.total_experiences,
                  }),
                  {
                    revenue: 0, profit: 0, transfers: 0, experiences: 0,
                  },
                );

                const granOptions = [
                  { key: 'day', label: 'Day' },
                  { key: 'week', label: 'Week' },
                  { key: 'month', label: 'Month' },
                  { key: 'quarter', label: 'Quarter' },
                ];

                return (
                  <div className="card shadow-sm rounded mt-4">
                    <div className="card-body p-4">
                      <div className="d-flex justify-content-between align-items-start mb-1 flex-wrap gap-2">
                        <h2 className="mb-0">Employee Performance</h2>
                        {employeePerfLoading && (
                          <span className="text-muted small">
                            <i className="fa fa-spin fa-spinner me-1" />
                            Loading…
                          </span>
                        )}
                      </div>
                      <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                        Revenue, profit, and booking counts (transfer / experience) by employee,
                        grouped by the selected granularity.
                        Bucketed by when each booking was created (not travel date).
                        Cancelled bookings excluded.
                      </p>

                      {/* Controls */}
                      <div className="d-flex flex-wrap gap-3 mb-3 align-items-end">
                        <div>
                          <div className="text-muted small mb-1">Granularity</div>
                          <div className="btn-group btn-group-sm" role="group">
                            {granOptions.map((opt) => (
                              <button
                                key={opt.key}
                                type="button"
                                className={`btn ${employeePerfGranularity === opt.key ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => {
                                  setEmployeePerfGranularity(opt.key);
                                  fetchEmployeePerformance(opt.key, employeePerfPeriods);
                                }}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div className="text-muted small mb-1">Periods back</div>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            style={{ width: '90px' }}
                            value={employeePerfPeriods}
                            min={1}
                            max={52}
                            onChange={(e) => setEmployeePerfPeriods(
                              parseInt(e.target.value, 10) || 1,
                            )}
                            onBlur={() => fetchEmployeePerformance(
                              employeePerfGranularity,
                              employeePerfPeriods,
                            )}
                          />
                        </div>
                        <div style={{ minWidth: '200px', flex: '1 1 auto' }}>
                          <div className="text-muted small mb-1">
                            Employees
                            {employeePerfSelected.length > 0
                              && ` (${employeePerfSelected.length} selected)`}
                          </div>
                          <select
                            multiple
                            className="form-control form-control-sm"
                            value={employeePerfSelected}
                            onChange={(e) => {
                              const selected = Array.from(e.target.selectedOptions)
                                .map((o) => o.value);
                              setEmployeePerfSelected(selected);
                            }}
                            style={{ minHeight: '60px' }}
                          >
                            {allCreators.map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                          {employeePerfSelected.length > 0 && (
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 mt-1"
                              onClick={() => setEmployeePerfSelected([])}
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Table */}
                      {filteredRows.length === 0 ? (
                        <div className="text-muted text-center py-4">
                          No data for the selected period.
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-sm table-hover">
                            <thead>
                              <tr>
                                <th>Employee</th>
                                {periodLabels.map((label) => (
                                  <th key={label} className="text-end">{label}</th>
                                ))}
                                <th className="text-end">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredRows.map((row) => (
                                <tr key={row.created_by}>
                                  <td className="fw-semibold">{row.created_by}</td>
                                  {row.periods.map((p) => (
                                    <td key={p.periodKey} className="text-end">
                                      {p.total_bookings > 0 ? (
                                        <>
                                          <div>
                                            €
                                            {thousandSeparator(Math.round(p.revenue))}
                                          </div>
                                          <div className="text-muted" style={{ fontSize: '0.75em' }}>
                                            Profit: €
                                            {thousandSeparator(Math.round(p.profit))}
                                          </div>
                                          <div style={{ fontSize: '0.75em' }}>
                                            <span title="Transfers" className="me-2">
                                              <i className="fa fa-route text-warning me-1" />
                                              {p.transfer_count}
                                            </span>
                                            <span title="Experiences">
                                              <i className="fa fa-star text-primary me-1" />
                                              {p.experience_count}
                                            </span>
                                          </div>
                                        </>
                                      ) : (
                                        <span className="text-muted">—</span>
                                      )}
                                    </td>
                                  ))}
                                  <td className="text-end fw-semibold">
                                    <div>
                                      €
                                      {thousandSeparator(Math.round(row.total_revenue))}
                                    </div>
                                    <div className="text-muted" style={{ fontSize: '0.75em' }}>
                                      Profit: €
                                      {thousandSeparator(Math.round(row.total_profit))}
                                    </div>
                                    <div style={{ fontSize: '0.75em' }}>
                                      <i className="fa fa-route text-warning me-1" />
                                      {row.total_transfers}
                                      <i className="fa fa-star text-primary ms-2 me-1" />
                                      {row.total_experiences}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                              <tr className="table-light fw-bold">
                                <td>Totals</td>
                                {totalsByPeriod.map((t, i) => (
                                  <td key={periodKeys[i]} className="text-end">
                                    <div>
                                      €
                                      {thousandSeparator(Math.round(t.revenue))}
                                    </div>
                                    <div style={{ fontSize: '0.75em', fontWeight: 'normal' }}>
                                      <i className="fa fa-route text-warning me-1" />
                                      {t.transfer_count}
                                      <i className="fa fa-star text-primary ms-2 me-1" />
                                      {t.experience_count}
                                    </div>
                                  </td>
                                ))}
                                <td className="text-end">
                                  <div>
                                    €
                                    {thousandSeparator(Math.round(grandTotals.revenue))}
                                  </div>
                                  <div style={{ fontSize: '0.75em', fontWeight: 'normal' }}>
                                    <i className="fa fa-route text-warning me-1" />
                                    {grandTotals.transfers}
                                    <i className="fa fa-star text-primary ms-2 me-1" />
                                    {grandTotals.experiences}
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </>
          )}

        </>
      )}

      {canSeePax && analyticsStats && (
        <>
          {/* 4. Pax & Occupancy */}
          <div className="card shadow-sm rounded mt-4">
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-start mb-1">
                <h2 className="mb-0">Pax &amp; Occupancy</h2>
                <span className="text-muted" style={{ fontSize: '0.8rem' }}>Last 6 months</span>
              </div>
              <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>
                Passenger volumes, category breakdown, and day-of-week patterns.
              </p>

              {/* Total pax KPI */}
              <div className="mb-4">
                <span
                  className="text-muted"
                  style={{
                    fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: 600,
                  }}
                >
                  Total Passengers Transported
                </span>
                <div className="fw-bold text-primary" style={{ fontSize: '2.5rem', lineHeight: 1.1 }}>
                  {thousandSeparator(analyticsStats.pax_stats.total_pax)}
                </div>
              </div>

              <div className="row g-4">
                {/* Pax by month */}
                <div className="col-12 col-lg-6" style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
                  <h6 className="fw-bold mb-3">Passengers per Month</h6>
                  <Bar
                    data={{
                      labels: analyticsStats.pax_stats.by_month.map((m) => m.month),
                      datasets: [{
                        label: 'Passengers',
                        data: analyticsStats.pax_stats.by_month.map((m) => m.pax),
                        backgroundColor: 'rgba(13,110,253,0.7)',
                        borderColor: 'rgba(13,110,253,1)',
                        borderWidth: 1,
                      }],
                    }}
                    options={{
                      responsive: true,
                      plugins: { legend: { display: false } },
                      scales: { y: { beginAtZero: true } },
                    }}
                  />
                </div>

                {/* Pax by day of week */}
                <div className="col-12 col-lg-6" style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
                  <h6 className="fw-bold mb-3">
                    Bookings by Day of Week
                    {' '}
                    {analyticsStats.pax_stats.by_day_of_week.length > 0 && (() => {
                      const busiest = [...analyticsStats.pax_stats.by_day_of_week]
                        .sort((a, b) => b.bookings - a.bookings)[0];
                      return (
                        <span className="badge bg-primary ms-2" style={{ fontSize: '0.7rem', fontWeight: 400 }}>
                          Busiest:
                          {' '}
                          {busiest.day}
                        </span>
                      );
                    })()}
                  </h6>
                  <Bar
                    data={{
                      labels: analyticsStats.pax_stats.by_day_of_week.map((d) => d.day.slice(0, 3)),
                      datasets: [{
                        label: 'Bookings',
                        data: analyticsStats.pax_stats.by_day_of_week.map((d) => d.bookings),
                        backgroundColor: analyticsStats.pax_stats.by_day_of_week
                          .map((d, _, arr) => {
                            const max = Math.max(...arr.map((x) => x.bookings));
                            return d.bookings === max ? 'rgba(13,110,253,0.85)' : 'rgba(13,110,253,0.35)';
                          }),
                        borderWidth: 0,
                      }],
                    }}
                    options={{
                      responsive: true,
                      plugins: { legend: { display: false } },
                      scales: { y: { beginAtZero: true } },
                    }}
                  />
                </div>

                {/* By category */}
                <div className="col-12">
                  <h6 className="fw-bold mb-2">By Vehicle Category</h6>
                  <Table size="sm" hover responsive className="responsive-card-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Bookings</th>
                        <th>Total Pax</th>
                        <th>Avg Pax / Booking</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analyticsStats.pax_stats.by_category.slice(0, categoryLimit).map((c) => (
                        <tr key={c.category}>
                          <td data-label="Category" className="fw-semibold">{c.category}</td>
                          <td data-label="Bookings">{c.bookings}</td>
                          <td data-label="Total Pax">{c.total_pax}</td>
                          <td data-label="Avg Pax / Booking">{c.avg_pax.toFixed(1)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  {analyticsStats.pax_stats.by_category.length > categoryLimit && (
                    <div className="text-center mt-2">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setCategoryLimit((n) => n + 5)}
                      >
                        Load more
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Traffic Sources */}
              {canSeeGlobal && (analyticsStats.utm_stats?.length > 0
                || analyticsStats.channel_stats?.length > 0) && (
                <div className="mt-4 pt-4 border-top">
                  <h6 className="fw-bold mb-3">Traffic Sources</h6>
                  <div className="row g-4">

                    {/* UTM attribution pie + table */}
                    {analyticsStats.utm_stats?.length > 0 && (
                      <>
                        <div className="col-12 col-lg-5" style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
                          <p
                            className="text-muted mb-2"
                            style={{ fontSize: '0.8rem' }}
                          >
                            By traffic source (gclid = Google Ads, utm_source otherwise)
                          </p>
                          <PieChart
                            data={analyticsStats.utm_stats.map((u) => ({
                              name: u.source,
                              value: u.bookings,
                            }))}
                          />
                        </div>
                        <div className="col-12 col-lg-7">
                          <Table size="sm" hover className="responsive-card-table">
                            <thead>
                              <tr>
                                <th>Source</th>
                                <th>Bookings</th>
                                <th>Revenue</th>
                              </tr>
                            </thead>
                            <tbody>
                              {analyticsStats.utm_stats.map((u) => (
                                <tr key={u.source}>
                                  <td data-label="Source" className="fw-semibold">{u.source}</td>
                                  <td data-label="Bookings">{u.bookings}</td>
                                  <td data-label="Revenue">
                                    €
                                    {thousandSeparator(Math.round(u.revenue))}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>

                          {/* Channel (PH_DASH / PH_WEB) breakdown below */}
                          {analyticsStats.channel_stats?.length > 0 && (
                            <>
                              <p
                                className="text-muted mt-3 mb-1"
                                style={{ fontSize: '0.8rem' }}
                              >
                                By booking channel
                              </p>
                              <Table size="sm" hover className="responsive-card-table">
                                <thead>
                                  <tr>
                                    <th>Channel</th>
                                    <th>Bookings</th>
                                    <th>Revenue</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {analyticsStats.channel_stats.map((c) => (
                                    <tr key={c.channel}>
                                      <td data-label="Channel">{channelLabel(c.channel)}</td>
                                      <td data-label="Bookings">{c.bookings}</td>
                                      <td data-label="Revenue">
                                        €
                                        {thousandSeparator(Math.round(c.revenue))}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </>
                          )}
                        </div>
                      </>
                    )}

                    {/* Google Ads monthly bar chart */}
                    {analyticsStats.google_ads_stats?.by_month?.length > 0 && (
                      <div className="col-12" style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <p className="fw-semibold mb-0" style={{ fontSize: '0.9rem' }}>
                            Google Ads — monthly trend
                          </p>
                          <div className="d-flex gap-3">
                            <span className="text-muted" style={{ fontSize: '0.82rem' }}>
                              {analyticsStats.google_ads_stats.total_bookings}
                              {' '}
                              bookings total
                            </span>
                            <span className="fw-semibold" style={{ fontSize: '0.82rem' }}>
                              €
                              {thousandSeparator(
                                Math.round(analyticsStats.google_ads_stats.total_revenue),
                              )}
                            </span>
                          </div>
                        </div>
                        <Bar
                          data={{
                            labels: analyticsStats.google_ads_stats.by_month.map(
                              (m) => m.month,
                            ),
                            datasets: [
                              {
                                label: 'Bookings',
                                data: analyticsStats.google_ads_stats.by_month.map(
                                  (m) => m.bookings,
                                ),
                                backgroundColor: 'rgba(13,110,253,0.7)',
                                borderWidth: 0,
                                yAxisID: 'y',
                              },
                              {
                                label: 'Revenue (€)',
                                data: analyticsStats.google_ads_stats.by_month.map(
                                  (m) => m.revenue,
                                ),
                                backgroundColor: 'rgba(25,135,84,0.55)',
                                borderWidth: 0,
                                yAxisID: 'y1',
                              },
                            ],
                          }}
                          options={{
                            responsive: true,
                            plugins: { legend: { position: 'top' } },
                            scales: {
                              y: {
                                beginAtZero: true,
                                position: 'left',
                                title: { display: true, text: 'Bookings' },
                              },
                              y1: {
                                beginAtZero: true,
                                position: 'right',
                                grid: { drawOnChartArea: false },
                                title: { display: true, text: 'Revenue (€)' },
                              },
                            },
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default Stats;
